import { readBarcodes, LIVE_OPTS, DEEP_OPTS } from './zxing.js';

/** ต้องเรียกจากใน handler ที่ผู้ใช้กดเอง (ห้ามเรียกตอน mount) ไม่งั้น iOS ไม่ให้กล้อง */
export async function startCamera(videoEl) {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('เบราว์เซอร์นี้ใช้กล้องไม่ได้');
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: 'environment' }, // ideal ไม่ใช่ exact — exact จะ throw บนเครื่องที่มีแต่กล้องหน้า
      width: { ideal: 1920 },               // iOS สั่งโฟกัสไม่ได้ → ซื้อความละเอียดแทน
      height: { ideal: 1080 },
      frameRate: { ideal: 30, max: 30 },    // 60fps กินไฟเท่าตัวโดยไม่ช่วยการอ่าน
    },
  });

  const track = stream.getVideoTracks()[0];
  let caps = {};
  try { caps = track.getCapabilities ? track.getCapabilities() : {}; } catch { caps = {}; }

  // iOS เปิดให้คุม zoom (Safari 17+) แต่ไม่เปิด focusMode/focusDistance — zoom คือคันโยกเดียวที่มี
  const advanced = [];
  if (caps.zoom) {
    const z = Math.min(caps.zoom.max ?? 1, Math.max(caps.zoom.min ?? 1, 1.8));
    advanced.push({ zoom: z });
  }
  if (caps.focusMode?.includes?.('continuous')) advanced.push({ focusMode: 'continuous' }); // Android เท่านั้น
  if (advanced.length) { try { await track.applyConstraints({ advanced }); } catch { /* ไม่รองรับก็ช่างมัน */ } }

  videoEl.srcObject = stream;
  videoEl.setAttribute('playsinline', '');
  videoEl.muted = true;
  try { await videoEl.play(); } catch { /* iOS คืน AbortError ตอน re-acquire เร็ว ๆ */ }

  return { stream, track, caps };
}

export function stopCamera(stream, videoEl) {
  try { stream?.getTracks().forEach(t => t.stop()); } catch { /* ปิดไปแล้ว */ }
  if (videoEl) { try { videoEl.srcObject = null; } catch { /* ignore */ } }
}

export const torchSupported = track => {
  try { return track?.getCapabilities?.().torch === true; } catch { return false; }
};

export async function setTorch(track, on) {
  if (!torchSupported(track)) return false;
  try { await track.applyConstraints({ advanced: [{ torch: !!on }] }); return true; }
  catch { return false; }
}
// เก็บสถานะไฟฉายเป็น state ของเราเอง — getSettings().torch คืนค่าเก่าค้างบน Safari รุ่นก่อน 18.4

const DECODE_EVERY_MS = 120;   // ~8 ครั้ง/วินาที พอสำหรับถือมือยิง
const ROI_FRACTION = 0.9;      // สี่เหลี่ยมจัตุรัสกลางเฟรม — รับได้ทั้งป้ายแนวนอนและป้ายวางตะแคง
const DEEP_AFTER_MS = 2500;

/** คืนฟังก์ชัน stop(); onHit(text, format) ถูกเรียกทุกครั้งที่อ่านได้ */
export function runScanLoop(videoEl, onHit) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  let stopped = false, busy = false, last = 0, missSince = 0, handle = 0, timer = 0;

  async function tick(now) {
    if (stopped) return;
    if (busy || now - last < DECODE_EVERY_MS) return;
    last = now; busy = true;
    try {
      const vw = videoEl.videoWidth, vh = videoEl.videoHeight;
      if (!vw || !vh) return;
      // ครอปที่ความละเอียดจริง ห้ามย่อ — EAN-13 กว้าง 95 module ต้องได้ >= 3 px ต่อ module
      const side = Math.max(1, Math.round(Math.min(vw, vh) * ROI_FRACTION));
      const sx = Math.round((vw - side) / 2), sy = Math.round((vh - side) / 2);
      if (canvas.width !== side || canvas.height !== side) { canvas.width = side; canvas.height = side; }
      ctx.drawImage(videoEl, sx, sy, side, side, 0, 0, side, side);
      const img = ctx.getImageData(0, 0, side, side);

      if (!missSince) missSince = now;
      const deep = now - missSince > DEEP_AFTER_MS;
      const res = await readBarcodes(img, deep ? DEEP_OPTS : LIVE_OPTS);
      if (deep) missSince = now;
      if (stopped) return;

      const hit = res.find(r => r.isValid && r.text);
      if (hit) { missSince = now; onHit(hit.text, hit.format); }
    } catch (e) {
      console.warn('[scan] decode error', e);
    } finally { busy = false; }
  }

  // requestVideoFrameCallback หยุดเองเมื่อแอปถูกพับไปหลัง (setInterval ไม่หยุด = กล้องร้อน+แบตหมด)
  if (typeof videoEl.requestVideoFrameCallback === 'function') {
    const onFrame = now => { if (stopped) return; handle = videoEl.requestVideoFrameCallback(onFrame); tick(now); };
    handle = videoEl.requestVideoFrameCallback(onFrame);
    return () => { stopped = true; try { videoEl.cancelVideoFrameCallback(handle); } catch { /* ignore */ } };
  }
  // สำรองสำหรับเบราว์เซอร์ที่ยังไม่มี rVFC (Firefox)
  timer = setInterval(() => tick(performance.now()), DECODE_EVERY_MS);
  return () => { stopped = true; clearInterval(timer); };
}

/** ภาพนิ่งจากกล้องที่เปิดอยู่ (ใช้เก็บรูปป้ายสินค้าที่ยังไม่มีใน PD) → Blob */
export function grabStill(videoEl, maxW = 1280, quality = 0.82) {
  const vw = videoEl?.videoWidth, vh = videoEl?.videoHeight;
  if (!vw || !vh) return Promise.resolve(null);
  const scale = Math.min(1, maxW / vw);
  const c = document.createElement('canvas');
  c.width = Math.round(vw * scale);
  c.height = Math.round(vh * scale);
  c.getContext('2d').drawImage(videoEl, 0, 0, c.width, c.height);
  return new Promise(r => c.toBlob(r, 'image/jpeg', quality));
}
